import type { Interval } from '@/types/Interval';
import { INTERVAL_LABELS } from '@/utils/enums/IntervalLabels';
import dayjs from 'dayjs';
import settings from '../../config/settings';

// Long-form display date, e.g. "June 30, 2025". Distinct from the numeric
// `dateFormat` in app.config.ts; declared here so this helper stays a pure
// function usable outside of the Nuxt app context.
export const LONG_DATE_FORMAT = 'MMMM D, YYYY';

export function $formatToDigitalTime(seconds: number): string {
  if (seconds < 0) {
    seconds = 0;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function $formatToReadableFileTypes(fileTypes: string[]): string {
  const types = fileTypes.map((type) => type.split('/')[1]);

  // Format special cases
  types[
    types.indexOf('vnd.openxmlformats-officedocument.wordprocessingml.document')
  ] = 'docx';

  return types.join(', ');
}

export function $intervalLabel(interval: Interval): string {
  return INTERVAL_LABELS[interval] || interval;
}

export function $fractionFormat() {
  return new Intl.NumberFormat(settings.currency.locale, {
    style: 'currency',
    currency: settings.currency.label,
    minimumFractionDigits: 0,
  });
}

export function $numberFormat() {
  return new Intl.NumberFormat(settings.currency.locale, {
    style: 'currency',
    currency: settings.currency.label,
    minimumFractionDigits: 2,
  });
}

export function $centsToHundredths(price: number): number {
  return price / 100;
}

export function $priceFormatter(
  price: number,
  inDecimal: boolean = false,
): string {
  return price % 1 !== 0 || inDecimal
    ? $numberFormat().format(price)
    : $fractionFormat().format(price);
}

export function $formatInDecimal(price: number): string {
  return $numberFormat().format(price);
}

export function $formatDate(
  date?: string | null,
  format: string = LONG_DATE_FORMAT,
): string {
  if (!date) return '';

  const parsed = dayjs(date);

  return parsed.isValid() ? parsed.format(format) : '';
}
